/**
 * `application/problem+json` — RFC 9457. shared-contract.md §3.
 *
 * A `409` **carries the conflicting record** in an extension member. Both
 * clients need it: the mobile Pending screen offers *same car / replaced /
 * wrong bay* from it, and the correct-a-pairing screen names the existing
 * pairing from it. A bare `409` makes those screens unbuildable.
 */

export const PROBLEM_BASE = 'https://api.udcpark.ing/problems';

export const PROBLEM_SLUGS = [
  'bay-occupied',
  'device-already-bound',
  'unknown-bay',
  'permission-denied',
  'out-of-scope',
  'precondition-failed',
  'validation-failed',
  'local-signin-disabled',
  'invalid-role-assignment',
  'calibration-band-invalid',
  'role-missing-read-permission',
  'download-link-expired',
  'export-row-cap-exceeded',
  'display-not-configured',
  'bay-change-needs-confirmation',
] as const;

export type ProblemSlug = (typeof PROBLEM_SLUGS)[number];

export function problemType(slug: ProblemSlug): string {
  return `${PROBLEM_BASE}/${slug}`;
}

export interface ProblemBody {
  type: string;
  title: string;
  status: number;
  detail?: string;
  traceId?: string;
  /** Extension members — `existingSession`, `existingPairing`, `errors`, … */
  [key: string]: unknown;
}

/**
 * The typed error every failed call throws. `traceId` is surfaced in small type
 * by `<ErrorState>` because support cannot work without it.
 */
export class ApiProblem extends Error {
  readonly type: string;
  readonly title: string;
  readonly status: number;
  readonly detail: string | undefined;
  readonly traceId: string | undefined;
  readonly body: ProblemBody;
  /** `Retry-After` in seconds, present on a 429. */
  readonly retryAfterSeconds: number | undefined;

  constructor(body: ProblemBody, options: { retryAfterSeconds?: number } = {}) {
    super(body.detail ?? body.title ?? `Request failed with ${body.status}`);
    this.name = 'ApiProblem';
    this.type = body.type;
    this.title = body.title;
    this.status = body.status;
    this.detail = body.detail;
    this.traceId = body.traceId;
    this.body = body;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }

  /** `slug` of the problem type, or `null` when it is not one of ours. */
  get slug(): ProblemSlug | null {
    const tail = this.type.startsWith(`${PROBLEM_BASE}/`)
      ? this.type.slice(PROBLEM_BASE.length + 1)
      : this.type.split('/').pop();
    return (PROBLEM_SLUGS as readonly string[]).includes(tail ?? '') ? (tail as ProblemSlug) : null;
  }

  is(slug: ProblemSlug): boolean {
    return this.slug === slug;
  }

  /** Extension member, typed by the caller that knows the shape. */
  extension<T>(key: string): T | undefined {
    return this.body[key] as T | undefined;
  }
}

export function isApiProblem(error: unknown): error is ApiProblem {
  return error instanceof ApiProblem;
}
